import { useState } from 'react';

import { ChevronDownIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import type { ColumnOption } from '@/modules/analytics/constants/metricsCatalog';

type Props = {
  id?: string;
  options: ColumnOption[];
  value: string[];
  onChange: (columns: string[]) => void;
  disabled?: boolean;
  className?: string;
};

export const GroupBySelect = ({ id, options, value, onChange, disabled, className }: Props) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const selected = options.filter((opt) => value.includes(opt.column));
  const allSelected = options.length > 0 && selected.length === options.length;
  let selectAllState: boolean | 'indeterminate' = false;
  if (allSelected) {
    selectAllState = true;
  } else if (selected.length > 0) {
    selectAllState = 'indeterminate';
  }

  const toggle = (column: string) =>
    onChange(value.includes(column) ? value.filter((c) => c !== column) : [...value, column]);

  const toggleAll = () => onChange(allSelected ? [] : options.map((opt) => opt.column));

  const rowClass =
    'flex w-full cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm leading-5 text-foreground hover:bg-muted/50';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          id={id}
          disabled={disabled}
          className={cn(
            'group flex h-9 min-w-0 items-center justify-between gap-2 rounded-md border border-border bg-background px-3 text-sm transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
            className
          )}
        >
          <span className="truncate text-left text-foreground">
            {selected.length > 0
              ? selected.map((opt) => opt.label).join(', ')
              : t('analytics.reports_detail_select_placeholder')}
          </span>
          <ChevronDownIcon className="size-4 shrink-0 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-(--radix-popover-trigger-width) p-1">
        <ul role="listbox" aria-multiselectable className="flex flex-col">
          <li>
            <label className={rowClass}>
              <Checkbox checked={selectAllState} onCheckedChange={toggleAll} />
              {t('analytics.reports_detail_group_by_select_all')}
            </label>
          </li>
          {options.map((opt) => (
            <li key={opt.column}>
              <label className={rowClass}>
                <Checkbox
                  checked={value.includes(opt.column)}
                  onCheckedChange={() => toggle(opt.column)}
                />
                {opt.label}
              </label>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
};
